// Imports ------------------------------------------------------------------->
import {Component, attr, handler} from "../Component";
import {element} from "../CustomElement";
import {i18n} from "../../i18n/I18n";
import * as array from "../../utils/array";
import {findSearchItems} from "./items";
import {LazyImage} from "../flipp-lazy-image/LazyImage";

import {inject} from "../../services/di";
import {LocationService} from "../../services/LocationService";
import {FlyerService} from "../../services/FlyerService";

import {AutocompleteContext} from "../../definitions/CommonDefinitions";

// Private Definitions ------------------------------------------------------->

// Public Definitions -------------------------------------------------------->

interface AutocompleteProps extends JSX.HTMLProps {
	value: string;
	context: AutocompleteContext;
	handleItemPicked: (item: string, idx: number) => void;
}

interface ItemInfo {
	name: string;
	itemId: string;
	logo?: string;
	flyerCount?: number;
}

type AutocompleteModel = {
	selected: ItemInfo,
	items: [ItemInfo[], ItemInfo[]]
};

const NUM_ITEMS_PER_CATEGORY = 3;
@element("flipp-searchbox-autocomplete")
export class Autocomplete extends Component<AutocompleteProps, AutocompleteModel> {
	@attr value: string;
	@attr context: AutocompleteContext;
	@inject location: LocationService;
	@inject flyerService: FlyerService;

	@handler handleItemPicked: (item: string, idx: number) => void;

	state = {
		focusIndex: -1,
		deleting: false
	};

	get focusIndex() {
		return this.state.focusIndex;
	}

	get hasResults() {
		return this.model &&
			(this.model.items[0].length > 0 || this.model.items[1].length);
	}

	get selectedItem() {
		return this.model ? this.model.selected : null;
	}

	prepare() {
		return Promise.all([
			this.findSearchItems_(this.value),

			// For SL autocomplete, we do NOT display merchants.
			// ------------------------------------------------------------
			(this.context === AutocompleteContext.GLOBAL_SEARCH)
				? this.flyerService.getFlyers()
				: Promise.resolve([])
		]).then(([items, flyers]) => {

			const merchants =
					array.groupBy(flyers, (flyer) => flyer.merchant.trim())
							.filter(([merchant]) => this.value &&
								merchant.toLowerCase().indexOf(this.value.toLowerCase()) === 0)
							.slice(0, NUM_ITEMS_PER_CATEGORY)
							.map(([merchant, flyers]) => ({
								itemId: `merchant-${flyers[0].merchantId}`,
								name: merchant,
								logo: flyers[0].merchantLogo,
								flyerCount: flyers.length
							}));

			const modelItems = [merchants, items];

			/* Identify the selected item based on focusIndex in state */
			let selectedItem: ItemInfo = null;
			if (!this.state.deleting && this.state.focusIndex >= 0) {
				if (this.state.focusIndex > (modelItems[0].length - 1)) {
					selectedItem = modelItems[1][this.state.focusIndex - modelItems[0].length];
				} else {
					selectedItem = modelItems[0][this.state.focusIndex];
				}
			}

			return {
				selected: selectedItem,
				items: modelItems
			} as AutocompleteModel;
		});
	}

	onAttributeChange(name: string, oldVal: string, newVal: string) {
		/* Don't automatically re-render on attribute change
			* This is so that the parent can manually call invalidate() and
			* tap into the returned promise to know when the model has been updated
			*/
		if (name === "value") {
			/* Input has changed - reset the focus index */
			this.state.focusIndex = -1;

			/* Check if the user is deleting/backspacing by comparing the new and
				* old values. In this case, we want to track it via state so that we
				* don't provide autocomplete selection as part of the resulting model */
			if (newVal && oldVal && oldVal.indexOf(newVal) === 0
				&& newVal.length < oldVal.length) {
				this.state.deleting = true;
			}
		}
	}

	onRender() {
		this.state.deleting = false;
	}

	public adjustFocusedItem(delta: number, reset?: boolean) {
		if (this.state.focusIndex === -1 && this.model.selected) {
			this.state.focusIndex = 0;
		}

		this.state.focusIndex += delta;
		const numItems = this.model.items[0].length + this.model.items[1].length;
		if (this.state.focusIndex >= numItems) {
			this.state.focusIndex = 0;
		} else if (this.state.focusIndex < 0) {
			this.state.focusIndex = numItems - 1;
		}
	}

	renderLoading() {
		return <span>Loading...</span>;
	}

	render(model: AutocompleteModel) {
		const isGlobalSearch = this.context === AutocompleteContext.GLOBAL_SEARCH;
		const [searchMerchants, searchItems] = model.items;
		const selectedIdx = this.model.selected && this.state.focusIndex === -1 ?
			0 : this.state.focusIndex;

		return <div>
				{searchMerchants && searchMerchants.length > 0 && isGlobalSearch ?
					<section class="merchants">
						<h3>{i18n("autocomplete_separator_merchants")}</h3>
						<ul>
							{searchMerchants.map((merchantDetails, idx) =>
								<li>
									<a
										flex-horizontal
										disabled
										tabIndex={-1}
										data-item-name={merchantDetails.name}
										classList={{
											focused: selectedIdx === idx
										}}
										onClick={this.handleItemPicked.bind(this, merchantDetails.name, idx)}
									>
										<div class="merchant-wrapper" flex-vertical>
											<span class="merchant-name">{merchantDetails.name}</span>
											<span class="flyer-count">
											{merchantDetails.flyerCount === 1 ?
												i18n("autocomplete_flyer")
											:
												i18n("autocomplete_flyers", {
													num: String(merchantDetails.flyerCount)
												})
											}
											</span>
										</div>
										<LazyImage alt="" fit cover dataSrc={merchantDetails.logo} />
									</a>
								</li>
							)}
						</ul>
					</section>
				:
					null
				}

				{searchItems && searchItems.length > 0 ?
					<section class="items">
						{isGlobalSearch ? <h3>{i18n("autocomplete_separator_items")}</h3> : null}
						<ul>
							{/*
							We need to have 'disabled' parameter specified because, otherwise,
							after you type the first letter, the focus suddenly switches to
							the first item on this autocomplete list, which is bad. We want to
							keep the focus on the input box as to allow user to type as much as
							he/she wants before picking a plausible autocomplete term.
							Moreover, we use <a> instead of <button> specifically, because
							disabled parameter on a button also disables the onClick handler,
							which is not acceptable to us.
							*/}
							{searchItems.map((item, idx) =>
								<li flex-horizontal>
									<a
										flex-grow
										disabled
										tabIndex={-1}
										data-item-name={item.name}
										classList={{
											focused: selectedIdx === (idx + searchMerchants.length)
										}}
										onClick={this.handleItemPicked.bind(this, item.name,
											isGlobalSearch ? idx + searchMerchants.length : idx)}
									>
										<span>{item.name}</span>
									</a>
								</li>
							)}
						</ul>
					</section>
				:
					null
				}
		</div>;

	}

	private findSearchItems_(searchTerm: string): ItemInfo[] {
		const items = findSearchItems(this.location.locale, searchTerm, NUM_ITEMS_PER_CATEGORY);
		return items.map((item, idx) => ({name: item, itemId: `item-${idx}`} as ItemInfo));
	}
}
